import React, { useCallback } from 'react';

import { Forecast } from '@futrli-sdk/predict';
import { LoadingWizardScreen } from '@futrli/ui-components/components/sidebar/wizard/screens/loading-wizard-screen/loading-wizard-screen';
import { useDialogWizardState } from '@futrli/ui-components/components/dialog-wizard/shared/useDialogWizardState';
import { usePredictionsInfoContext } from '@futrli/predict/forecast/detail/context/predictions-info-context';

import { ForecastUpdatedWizardScreen } from './screens/forecast-updated-wizard-screen';
import { ForecastUpdatingWizardScreen } from './screens/forecast-updating-wizard-screen';
import { UpdateForecastFormWizardScreen } from './screens/update-forecast-form-wizard-screen';
import { useUpdateForecastContext } from '../context/update-forecast-context';

interface UpdateForecastContainerProps {
  onClose: () => void;
}

export const UpdateForecastContainer = ({ onClose }: UpdateForecastContainerProps) => {
  const { form, view } = useUpdateForecastContext();
  const { isConsolidation, syncForecast } = usePredictionsInfoContext();

  const validateSubmit = useCallback(() => form.isDirty && form.validate(), [form]);

  const handleSubmit = useCallback(() => {
    const updateRequest = view.updateForecast(form.getRequestData());

    if (!isConsolidation && syncForecast) {
      void updateRequest
        .then(updatedForecast => {
          if (updatedForecast) {
            syncForecast(updatedForecast as Forecast);
          }
        })
        .catch(() => undefined);
    }
  }, [form, isConsolidation, syncForecast, view]);

  const { screen, handleAction } = useDialogWizardState({
    isLoading: view.state.isLoading || view.state.isUpdating,
    error: view.state.errorUpdating,
    onAction: handleSubmit,
    onClose,
    validate: validateSubmit
  });

  switch (screen) {
    case 'loading': {
      return view.state.isLoading ? <LoadingWizardScreen /> : <ForecastUpdatingWizardScreen />;
    }
    case 'success': {
      return <ForecastUpdatedWizardScreen onClose={onClose} />;
    }
    default: {
      return (
        <UpdateForecastFormWizardScreen
          onClose={onClose}
          onUpdate={handleAction}
          error={view.state.errorUpdating?.message}
        />
      );
    }
  }
};
